import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

export default class extends Controller {
  static values = {
    selectForClient: { type: Boolean, default: false },
    autoNextSelection: { type: Boolean, default: false },
    skipComplete: { type: Boolean, default: false },
  }

  connect() {
    this.syncPageUrl()
    this.syncLinks()
    this.updateControls()
  }

  async toggleSetting(event) {
    event.preventDefault()

    const setting = event.params.setting
    if (!setting) return
    if (setting === "skipComplete" && !this.autoNextSelectionValue) return

    this[`${setting}Value`] = !this[`${setting}Value`]
    this.syncPageUrl()
    this.syncLinks()
    this.updateControls()
    await this.refreshCurrentSelection()
  }

  currentSelectionLink() {
    return this.element.querySelector(".bg-cyan-100 [data-selection-link='true']")
  }

  async refreshCurrentSelection() {
    const currentSelectionLink = this.currentSelectionLink()
    if (!currentSelectionLink) return

    await post(currentSelectionLink.href, {
      body: "",
      responseKind: "turbo-stream"
    })
  }

  syncLinks() {
    this.element
      .querySelectorAll("a[data-selection-link='true'], a[data-preserve-selection-settings='true']")
      .forEach((link) => {
        link.href = this.urlWithState(link.href)
      })
  }

  syncPageUrl() {
    window.history.replaceState({}, "", this.urlWithState(window.location.href))
  }

  updateControls() {
    this.updateControl("selectForClient", this.selectForClientValue)
    this.updateControl("autoNextSelection", this.autoNextSelectionValue)
    this.updateControl("skipComplete", this.skipCompleteValue, { disabled: !this.autoNextSelectionValue })
  }

  updateControl(setting, enabled, options = {}) {
    const button = this.element.querySelector(`[data-selection-mode-setting-button='${setting}']`)
    const track = this.element.querySelector(`[data-selection-mode-track='${setting}']`)
    const thumb = this.element.querySelector(`[data-selection-mode-thumb='${setting}']`)
    const disabled = options.disabled === true

    if (button) {
      button.disabled = disabled
      button.setAttribute("aria-checked", enabled.toString())
      button.classList.toggle("opacity-50", disabled)
      button.classList.toggle("cursor-not-allowed", disabled)
      button.classList.toggle("hover:bg-gray-50", !disabled)
    }

    if (track) {
      track.classList.toggle("bg-cyan-600", enabled && !disabled)
      track.classList.toggle("bg-gray-200", !enabled || disabled)
    }

    if (thumb) {
      thumb.classList.toggle("translate-x-5", enabled && !disabled)
      thumb.classList.toggle("translate-x-0", !enabled || disabled)
    }
  }

  urlWithState(urlString) {
    const url = new URL(urlString, window.location.origin)

    if (this.selectForClientValue) {
      url.searchParams.set("states[select_for_client]", "true")
    } else {
      url.searchParams.delete("states[select_for_client]")
    }

    if (this.autoNextSelectionValue) {
      url.searchParams.set("states[auto_next_selection]", "true")
    } else {
      url.searchParams.delete("states[auto_next_selection]")
    }

    if (this.skipCompleteValue) {
      url.searchParams.set("states[skip_complete]", "true")
    } else {
      url.searchParams.delete("states[skip_complete]")
    }

    return `${url.pathname}${url.search}${url.hash}`
  }
}
